<script lang="ts">
    import {Component, Prop, mixins, toNative} from 'vue-facing-decorator'
    import {TrainStationsSearchMixin} from '~/mixins/TrainStationsSearchMixin.vue'
    import {CruisePortsSearchMixin} from '~/mixins/CruisePortsSearchMixin.vue'
    import CityHotelAutocomplete from '~/components/search/forms/CityHotelAutocomplete.vue'

    @Component({
        mixins: [CityHotelAutocomplete],
    })
    export class TransferPointAutocomplete extends mixins(TrainStationsSearchMixin, CruisePortsSearchMixin) {
        @Prop({default: 'TRANSFER'}) productType
        @Prop() departureLocation

        selectionLabel(entry) {
            return (
                CityHotelAutocomplete.methods.selectionLabel(entry) +
                (entry.type === 'airport' ? ` (${entry.iataCode})` : '')
            )
        }

        async searchHotels(pattern) {
            const locationId = this.departureLocation
                ? this.departureLocation.type === 'city'
                    ? this.departureLocation.id
                    : this.departureLocation.cityId
                : null

            let rs = await this.$api.suggestHotels.get(
                {
                    pattern,
                    limit: 10,
                    ...(locationId && {locationId}),
                },
                this.cancelTokenSource.token
            )
            if (this.departureLocation && !rs.hotels.length) {
                rs = await this.$api.suggestHotels.get(
                    {
                        pattern,
                        limit: 10,
                        locationId: this.departureLocation.countryId,
                    },
                    this.cancelTokenSource.token
                )
            }
            if (this.departureLocation && !rs.hotels.length) {
                rs = await this.$api.suggestHotels.get(
                    {
                        pattern,
                        limit: 10,
                    },
                    this.cancelTokenSource.token
                )
            }
            return this.mapHotels(rs.hotels)
        }

        async initSearch(val) {
            if ((this.entries.length > 0 && this.entries.indexOf(val) !== -1) || !val) return
            this.loading = true
            if (this.cancelTokenSource) this.cancelTokenSource.cancel('Operation canceled by the user.')
            this.cancelTokenSource = this.$axios.CancelToken.source()

            try {
                const [airports, cities, hotels] = await Promise.all([
                    this.searchAirportsByCode(val),
                    this.searchCities(val),
                    this.searchHotels(val),
                ])
                this.entries = [...airports, ...cities, ...hotels]
                const citiesWithAirportsAndTrainStations = await Promise.all(cities.map(this.cityMapper))
                this.entries = [...airports, ...citiesWithAirportsAndTrainStations.flat(), ...hotels]

                if (!this.entries.length) {
                    this.tryResearch.status = true
                    this.tryResearch.message = this.$t('try_rewrite_destination')
                }
            } catch {
                /* empty */
            } finally {
                this.loading = false
            }
        }

        async cityMapper(city) {
            const [airports, trainStations, cruisePorts] = await Promise.all([
                this.searchAirports({
                    locationId: city.id,
                    limit: 0,
                    ...(!this.$conf.public.account.hasTransferExternalSuppliers && {productType: this.productType}),
                }),
                this.searchTrainStations({
                    locationId: city.id,
                    limit: 0,
                    ...(!this.$conf.public.account.hasTransferExternalSuppliers && {productType: this.productType}),
                }),
                this.searchCruisePorts({
                    locationId: city.id,
                    limit: 0,
                    ...(!this.$conf.public.account.hasTransferExternalSuppliers && {productType: this.productType}),
                }),
            ])
            trainStations.map(trainStation => {
                trainStation.cityName = city.name
                trainStation.countryName = city.countryName
                trainStation.parentName = city.countryName
                trainStation.countryId = city.countryId
            })

            return [
                ...[city],
                ...airports.map(item => ({...item, subLocation: true})),
                ...trainStations.map(item => ({...item, subLocation: true})),
                ...cruisePorts.map(item => ({...item, subLocation: true})),
            ]
        }

        async searchAirports(rq) {
            const defaults = {
                pattern: null,
                limit: 10,
                orderBy: 'POPULARITY',
            }

            //TODO Fix limit in API
            const rs = await this.$api.airports.get({...defaults, ...rq}, this.cancelTokenSource.token)

            return rs.map(airport => {
                return Object.assign(airport, {parentName: airport.countryName, type: 'airport'})
            })
        }

        async searchAirportsByCode(iataCode) {
            if (iataCode.length !== 3) return []
            try {
                return (await this.$api.airports.get({iataCode}, this.cancelTokenSource.token)).map(airport => ({
                    ...airport,
                    parentName: airport.countryName,
                    type: 'airport',
                }))
            } catch {
                return []
            }
        }

        icon(type) {
            switch (type) {
                case 'hotel':
                    return this.hotelIcon
                case 'airport':
                    return this.airportIcon
                case 'trainStation':
                    return this.trainStationIcon
                case 'cruisePort':
                    return this.cruisePortIcon
                default:
                    return this.locationIcon
            }
        }

        get airportIcon() {
            return 'mdi-airplane-takeoff'
        }

        get trainStationIcon() {
            return 'mdi-train'
        }

        get cruisePortIcon() {
            return 'mdi-ferry'
        }
    }

    export default {...toNative(TransferPointAutocomplete), ssrRender: CityHotelAutocomplete.ssrRender}
</script>

<style scoped lang="scss">
    :deep(.v-dialog) {
        overflow-y: hidden;
    }
    :deep(.v-label) {
        font-size: inherit;
    }

    :deep(.v-autocomplete__selection) {
        line-height: unset !important;
        height: unset !important;
        display: flex;
        align-items: start;
    }
</style>
